
import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loader2, Database, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useQuery } from '@tanstack/react-query';
import { useRoofingBrands } from '@/hooks/useRoofingBrands';
import { useRoofingServices } from '@/hooks/useRoofingServices';
import { useImageVariables } from '@/hooks/useImageVariables';

const DatabaseVariablesList: React.FC = () => {
  // Fetch content variables
  const { data: contentVariables, isLoading: contentLoading } = useQuery({
    queryKey: ['contentVariables'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('content_variables')
        .select('*')
        .order('category', { ascending: true });
      
      if (error) throw error;
      return data;
    },
  });

  // Fetch service locations
  const { data: serviceLocations, isLoading: locationsLoading } = useQuery({
    queryKey: ['serviceLocations'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('service_locations')
        .select('*')
        .order('name');
      
      if (error) throw error;
      return data;
    },
  });

  // Use existing hooks for other data
  const { data: roofingBrands, isLoading: brandsLoading } = useRoofingBrands();
  const { data: roofingServices, isLoading: servicesLoading } = useRoofingServices();
  const { data: imageVariables, isLoading: imagesLoading } = useImageVariables();

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Copied to clipboard!');
    } catch (error) {
      toast.error('Failed to copy');
    }
  };

  const isLoading = contentLoading || locationsLoading || brandsLoading || servicesLoading || imagesLoading;

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin mr-2" />
          Loading database variables...
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="h-5 w-5 text-blue-500" />
            Real Database Variables
          </CardTitle>
          <CardDescription>
            Actual variable placeholders available from your Supabase database
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          
          {/* Content Variables */}
          <div>
            <h3 className="font-semibold mb-3">Content Variables ({contentVariables?.length || 0})</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {contentVariables?.map((variable) => (
                <div key={variable.id} className="border rounded-lg p-3">
                  <div className="flex items-center justify-between mb-1">
                    <Badge 
                      variant="secondary" 
                      className="cursor-pointer hover:bg-blue-100"
                      onClick={() => copyToClipboard(variable.syntax)}
                    >
                      {variable.syntax}
                    </Badge>
                    <Copy className="h-4 w-4 text-muted-foreground cursor-pointer" 
                          onClick={() => copyToClipboard(variable.syntax)} />
                  </div>
                  <p className="text-sm text-muted-foreground">{variable.description}</p>
                  <p className="text-xs font-mono mt-1 truncate">{variable.value}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Service Locations */}
          <div>
            <h3 className="font-semibold mb-3">Service Locations ({serviceLocations?.length || 0})</h3>
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground mb-2">Available fields:</p>
              <div className="flex flex-wrap gap-2">
                {['name', 'county', 'population', 'coordinates', 'latitude', 'longitude', 'historical_fact', 'key_search_terms'].map((field) => (
                  <Badge 
                    key={field} 
                    variant="outline"
                    className="cursor-pointer hover:bg-green-100"
                    onClick={() => copyToClipboard(`{location.${field}}`)}
                  >
                    location.{field}
                  </Badge>
                ))}
              </div>
              <div className="mt-3">
                <p className="text-xs text-muted-foreground">Sample locations:</p>
                {serviceLocations?.slice(0, 3).map((location) => (
                  <div key={location.id} className="text-xs mt-1">
                    <strong>{location.name}</strong> - {location.county} (Pop: {location.population?.toLocaleString() || 'N/A'})
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Roofing Brands */}
          <div>
            <h3 className="font-semibold mb-3">Roofing Brands ({roofingBrands?.length || 0})</h3>
            <div className="space-y-2">
              <div className="flex flex-wrap gap-2">
                {['brand_name', 'top_products', 'warranty_info', 'website_url', 'seo_keywords', 'logo_url'].map((field) => (
                  <Badge 
                    key={field} 
                    variant="outline"
                    className="cursor-pointer hover:bg-purple-100"
                    onClick={() => copyToClipboard(`{brand.${field}}`)}
                  >
                    brand.{field}
                  </Badge>
                ))}
              </div>
              <div className="mt-3">
                <p className="text-xs text-muted-foreground">Sample brands:</p>
                {roofingBrands?.slice(0, 3).map((brand) => (
                  <div key={brand.id} className="text-xs mt-1">
                    <strong>{brand.brand_name}</strong>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Roofing Services */}
          <div>
            <h3 className="font-semibold mb-3">Roofing Services ({roofingServices?.length || 0})</h3>
            <div className="space-y-2">
              <div className="flex flex-wrap gap-2">
                {['service_name', 'description', 'category', 'seo_keywords', 'reference_urls'].map((field) => (
                  <Badge 
                    key={field} 
                    variant="outline"
                    className="cursor-pointer hover:bg-orange-100"
                    onClick={() => copyToClipboard(`{service.${field}}`)}
                  >
                    service.{field}
                  </Badge>
                ))}
              </div>
              <div className="mt-3">
                <p className="text-xs text-muted-foreground">Sample services:</p>
                {roofingServices?.slice(0, 3).map((service) => (
                  <div key={service.id} className="text-xs mt-1">
                    <strong>{service.service_name}</strong> - {service.category}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Image Variables */}
          <div>
            <h3 className="font-semibold mb-3">Image Variables ({imageVariables?.length || 0})</h3>
            <div className="space-y-2">
              <div className="flex flex-wrap gap-2">
                {['variable_name', 'description', 'alt_tag', 'meta_description', 'keywords', 'use_case', 'schema_snippet', 'image_url'].map((field) => (
                  <Badge 
                    key={field} 
                    variant="outline"
                    className="cursor-pointer hover:bg-red-100"
                    onClick={() => copyToClipboard(`{image.${field}}`)}
                  >
                    image.{field}
                  </Badge>
                ))}
              </div>
              <div className="mt-3">
                <p className="text-xs text-muted-foreground">Sample image variables:</p>
                {imageVariables?.slice(0, 3).map((image) => (
                  <div key={image.id} className="text-xs mt-1">
                    <strong>{image.variable_name}</strong> - {image.description}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="text-xs text-muted-foreground p-3 bg-blue-50 rounded-md">
            <p className="font-medium mb-1">💡 How to use:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>Click any variable to copy it to clipboard</li>
              <li>Content variables use the exact syntax shown (e.g., {`{businessName}`})</li>
              <li>Other data requires custom mapping in your code</li>
              <li>This shows what's actually in your database right now</li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DatabaseVariablesList;
